import React, { useState, useEffect } from 'react';
import { useFirebase } from '../FirebaseContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './MojeDokumenty.css';

const MojeDokumenty = () => {
  const { currentFirebase } = useFirebase();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (currentFirebase && currentFirebase.firestore) {
      loadDocuments();
    }
  }, [currentFirebase]);

  const loadDocuments = async () => {
    if (!currentFirebase || !currentFirebase.firestore) {
      console.error('Firestore not available');
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const currentUser = currentFirebase.auth?.currentUser;
      
      if (!currentUser) {
        console.log('Użytkownik nie jest zalogowany');
        // W trybie demo możemy pokazać wszystkie dokumenty
        const documentsCollection = collection(currentFirebase.firestore, 'generatedDocuments');
        const querySnapshot = await getDocs(documentsCollection);
        const documentsData = [];
        
        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          documentsData.push({ 
            id: docSnapshot.id, 
            ...data,
            generatedAt: data.generatedAt?.toDate() || new Date()
          });
        });
        
        // Сортировка на kliencie
        documentsData.sort((a, b) => b.generatedAt - a.generatedAt);
        setDocuments(documentsData);
      } else {
        // Jeśli użytkownik jest zalogowany, filtrujemy po userId
        const documentsCollection = collection(currentFirebase.firestore, 'generatedDocuments');
        const q = query(
          documentsCollection,
          where('userId', '==', currentUser.uid)
        );

        const querySnapshot = await getDocs(q);
        const documentsData = [];
        
        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          documentsData.push({ 
            id: docSnapshot.id, 
            ...data,
            generatedAt: data.generatedAt?.toDate() || new Date()
          });
        });
        
        // Сортировка на kliencie
        documentsData.sort((a, b) => b.generatedAt - a.generatedAt);
        setDocuments(documentsData);
      }
    } catch (error) {
      console.error('Błąd podczas ładowania dokumentów:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (document) => {
    // Otwieramy dokument w nowej karcie
    window.open(document.fileUrl, '_blank');
  };

  const handlePrint = (document) => {
    // Tworzymy iframe do drukowania
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = document.fileUrl;
    document.body.appendChild(iframe);
    
    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesFilter = filter === 'all' || doc.templateName?.toLowerCase().includes(filter.toLowerCase());
    const matchesSearch = doc.templateName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const documentTypes = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'umowa', label: 'Umowy' },
    { value: 'faktura', label: 'Faktury' },
    { value: 'raport', label: 'Raporty' },
    { value: 'akt', label: 'Akty' }
  ];

  // Если Firestore не загружен, показываем загрузку
  if (!currentFirebase || !currentFirebase.firestore) {
    return (
      <div className="moje-dokumenty-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Ładowanie połączenia z bazą danych...</p>
          <p style={{ fontSize: '14px', marginTop: '10px', color: '#666' }}>
            Sprawdź czy wybrałeś projekt w górnym menu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="moje-dokumenty-container">
      <div className="dokumenty-header">
        <h1>Moje dokumenty</h1>
        <div className="header-info">
          <span>Łącznie dokumentów: {documents.length}</span>
        </div>
      </div>

      <div className="dokumenty-filters">
        <div className="filter-group">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            {documentTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        
        <div className="search-group">
          <input
            type="text"
            placeholder="Szukaj dokumentu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Ładowanie dokumentów...</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>Brak dokumentów</h3>
          <p>Nie znaleziono żadnych dokumentów spełniających kryteria wyszukiwania.</p>
          {!currentFirebase.auth?.currentUser && (
            <p className="text-muted" style={{ fontSize: '14px', marginTop: '10px', color: '#888' }}>
              Uwaga: Wyświetlane są wszystkie dokumenty. Zaloguj się aby zobaczyć tylko swoje.
            </p>
          )}
        </div>
      ) : (
        <div className="documents-grid">
          {filteredDocuments.map(document => (
            <div key={document.id} className="document-card">
              <div className="document-icon">
                <i className="fas fa-file-pdf"></i>
              </div>
              
              <div className="document-info">
                <h3>{document.templateName || 'Dokument bez nazwy'}</h3>
                <p className="document-date">
                  Wygenerowano: {formatDate(document.generatedAt)}
                </p>
                {!currentFirebase.auth?.currentUser && document.userId && (
                  <p className="document-user" style={{ fontSize: '12px', color: '#666' }}>
                    User ID: {document.userId.substring(0, 8)}...
                  </p>
                )}
              </div>

              <div className="document-actions">
                <button 
                  onClick={() => handleDownload(document)}
                  className="action-button download"
                  title="Pobierz dokument"
                  disabled={!document.fileUrl}
                >
                  <i className="fas fa-download"></i>
                  Pobierz
                </button>
                
                <button 
                  onClick={() => handlePrint(document)}
                  className="action-button print"
                  title="Drukuj dokument"
                  disabled={!document.fileUrl}
                >
                  <i className="fas fa-print"></i>
                  Drukuj
                </button>
                
                <button 
                  onClick={() => window.open(document.fileUrl, '_blank')}
                  className="action-button view"
                  title="Podgląd dokumentu"
                  disabled={!document.fileUrl}
                >
                  <i className="fas fa-eye"></i>
                  Podgląd
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="documents-info">
        <div className="info-card">
          <i className="fas fa-info-circle"></i>
          <div>
            <h4>Informacje o dokumentach</h4>
            <p>Wszystkie dokumenty są generowane automatycznie na podstawie aktualnych danych.</p>
            <p>Dokumenty są przechowywane w bezpieczny sposób i dostępne tylko dla Ciebie.</p>
            <p>W przypadku pytań lub problemów skontaktuj się z administratorem.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MojeDokumenty;